import { BackButton, PrimaryButtonMTV2 } from '@/components/buttons/Buttons';
import { showNotification } from '@/modules/notification/engine/notification.library';
import { useNavigate } from 'react-router-dom';
import { Dialog, type DialogProps } from 'primereact/dialog';
import { useState } from 'react';
import AddGroupForm from '../components/group/AddGroupForm';
import style from '../views/group.module.css';
import { useForm } from 'react-hook-form';
import { axiosInstance } from '@/services/axios.service';
import { ENDPOINTS } from '@/constants/endpoints';
import { GenericResponse } from '@/common/common_utils';
import { Column } from '@/components/container/Container';
import { TextHeadlineLarge, TextTitleMedium2 } from '@/components/text/Texts';
import { cn } from '@/components/ui/cn';
import { yupResolver } from '@hookform/resolvers/yup';
import { number, object, string } from 'yup';
import { VALIDATION_MESSAGES } from '@/constants/validation';

export interface CreateGroupRequest {
	Name: string;
	Description: string;
	ProgramId: number;
}

export interface CreateGroupResponse {
	Id: number;
}

const AddGroupScreen = (props: DialogProps) => {
	const { visible, onHide, ...rest } = props;
	const [activeIndex, setActiveIndex] = useState<number>(0);
	const navigate = useNavigate();

	const form = useForm<CreateGroupRequest>({
		mode: 'all',
		defaultValues: { Name: '', Description: '', ProgramId: 0 },
		resolver: yupResolver(object({
			Name: string().trim().required(VALIDATION_MESSAGES.required),
			Description: string().trim().required(VALIDATION_MESSAGES.required),
			ProgramId: number().required(VALIDATION_MESSAGES.required),
		})),
	});

	const onNext = () => setActiveIndex((prev) => prev + 1);
	const onBack = () => activeIndex === 0 ? onHide() : setActiveIndex((prev) => prev - 1);
	const handleSubmit = async () => {
		const formValues = form.getValues();
		const response = await axiosInstance.post<GenericResponse<CreateGroupResponse>>(ENDPOINTS['/api/X4bGroup/CreateGroup'], formValues);

		onHide();
		navigate(`/people/group/${response.data.Data.Id}`);
		showNotification(
			'Congrats. Your new group is ready. You can begin adding people to this group.',
			{}, undefined,
			{
				pt: {
					root: { className: '!bottom-2 !left-6' },
					content: { className: 'w-[692px] h-[52px] px-4 !py-0' }
				}
			}
		);
	};

	const headerTemplate = (
		<Column className="gap-2">
			<BackButton
				withIcon
				label="Back"
				className="self-start"
				id={'back-add-group'}
				onClick={onBack}
			/>
			<TextHeadlineLarge className="text-neutral17" text="Create a new group" />
			{activeIndex === 1 && (
				<TextTitleMedium2 className="text-onSurfaceVariant" text="Give this group a name and description" />
			)}
		</Column>
	);

	const footerTemplate = (
		<PrimaryButtonMTV2
			id={'add-program'}
			onClick={activeIndex === 0 ? onNext : handleSubmit}
			label={activeIndex === 0 ? "Next" : "Finish"}
			disabled={activeIndex === 0
				? !form.watch('ProgramId')
				: !form.watch('Name')?.trim() || !form.watch('Description')?.trim()}
		/>
	);

	return (
		<Dialog
			{...rest}
			onHide={onHide}
			visible={visible}
			header={headerTemplate}
			footer={footerTemplate}
			draggable={false}
			className="w-[1136px] tablet:!w-[calc(100vw-6rem)] tablet:!max-w-[calc(100vw-6rem)]"
			pt={{
				root: { className: cn(style['group-add-modal-top'], style['group-add-modal-bottom'], style['group-add-modal-root']) },
				header: { className: cn(style['group-add-modal-header'], style['group-add-modal-top']) },
				content: { className: cn(style['group-add-modal-content']) },
				footer: { className: cn(style['group-add-modal-bottom'], style['group-add-modal-footer']) },
			}}
		>
			<AddGroupForm activeIndex={activeIndex} form={form} />
		</Dialog>
	);
};

export default AddGroupScreen;
